import { Injectable } from '@angular/core';

import { ApolloQueryResult, FetchPolicy } from '@apollo/client/core';
import { Apollo, gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';

import { Request } from './request-enum';

@Injectable({ providedIn: 'root' })
export class CollectionsService {
  constructor(private apollo: Apollo) {}

  query<T>(
    request: Request,
    variables?: { [k: string]: string | number | boolean | string[] },
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<T> {
    return this.apollo
      .query({
        query: gql`
          ${request}
        `,
        variables,
        fetchPolicy,
      })
      .pipe(map((result: ApolloQueryResult<any>): T => this.flattenResult(result)));
  }

  private flatten(object: any): any {
    if (!object || typeof object !== 'object') {
      return object;
    }
    if (Array.isArray(object)) {
      return object.map((item: any) => this.flatten(item));
    }
    if (Array.isArray(object.data)) {
      return object.data.map((item: any) => this.flatten(item));
    }

    const id = object.data?.id || object.id;
    const rebuiltObject: { [key: string]: any } = object.data?.attributes
      ? { ...object.data.attributes }
      : object.attributes
        ? { ...object.attributes }
        : { ...object };

    Object.keys(rebuiltObject).forEach(key => {
      const dataToFlatten = rebuiltObject[key];
      if (dataToFlatten && typeof dataToFlatten === 'object') {
        rebuiltObject[key] = this.flatten(dataToFlatten);
      }
    });

    if (id) {
      rebuiltObject.id = String(Number(id)) === id ? Number(id) : id;
    }

    return rebuiltObject;
  }

  private flattenResult<T>(result: ApolloQueryResult<any>): T {
    const data: { [key: string]: any } = {};

    Object.keys(result.data).forEach(key => {
      data[key] = this.flatten(result.data[key]);
    });

    return data as T;
  }
}
